import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  MdInsights,
  MdMenuBook,
  MdPeopleAlt,
  MdQuiz,
  MdSchedule,
  MdSearch,
  MdTrendingUp,
  MdCheckCircle,
  MdVisibility,
} from "react-icons/md";
import Card from "components/card";
import EmptyState from "components/ui/EmptyState";
import SlideOver from "components/ui/SlideOver";
import DataTablePagination from "components/ui/DataTablePagination";
import { Skeleton } from "components/ui/Skeleton";
import { useAppLanguage } from "hooks/useAppLanguage";
import {
  learningAnalyticsService,
  type AdminLearningOverview,
  type CourseAnalytics,
  type StudentLearningSummary,
} from "services/learningAnalyticsService";
import {
  DATA_TABLE_BODY_ROW,
  DATA_TABLE_HEADER_ROW,
  DATA_TABLE_SCROLL,
  dataTableBodyCellClass,
  dataTableHeaderCellClass,
} from "lib/dataTableClasses";

function useDebounced<T>(value: T, delay = 350) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  return value.replace("T", " ").slice(0, 16);
}

function SummaryCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  tone: string;
}) {
  return (
    <div className={`rounded-2xl border border-gray-200 bg-gradient-to-br p-4 shadow-sm dark:border-white/10 ${tone}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 dark:bg-navy-800">{icon}</div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-bold text-gray-800 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function LearningAnalyticsPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [searchParams] = useSearchParams();
  const initialCourseId = Number(searchParams.get("course_id") || 0);

  const [overview, setOverview] = React.useState<AdminLearningOverview | null>(null);
  const [coursesPage, setCoursesPage] = React.useState(1);
  const [courseSearch, setCourseSearch] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [studentIdInput, setStudentIdInput] = React.useState("");
  const debouncedCourseSearch = useDebounced(courseSearch);

  const [courseDetailOpen, setCourseDetailOpen] = React.useState(false);
  const [courseDetail, setCourseDetail] = React.useState<CourseAnalytics | null>(null);
  const [courseDetailLoading, setCourseDetailLoading] = React.useState(false);

  const [studentSummaryOpen, setStudentSummaryOpen] = React.useState(false);
  const [studentSummary, setStudentSummary] = React.useState<StudentLearningSummary | null>(null);
  const [studentSummaryLoading, setStudentSummaryLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await learningAnalyticsService.adminOverview({
          page: coursesPage,
          per_page: 20,
          search: debouncedCourseSearch.trim() || undefined,
        });
        if (!cancelled) setOverview(data);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.message ?? (isArabic ? "تعذر تحميل التحليلات" : "Failed to load analytics"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [coursesPage, debouncedCourseSearch, isArabic]);

  React.useEffect(() => {
    setCoursesPage(1);
  }, [debouncedCourseSearch]);

  const openCourseDetail = async (courseId: number) => {
    setCourseDetailOpen(true);
    setCourseDetailLoading(true);
    setCourseDetail(null);
    try {
      const data = await learningAnalyticsService.adminCourseAnalytics(courseId);
      setCourseDetail(data);
    } finally {
      setCourseDetailLoading(false);
    }
  };

  const openStudentSummary = async () => {
    const lookup = studentIdInput.trim();
    if (!lookup) return;
    setStudentSummaryOpen(true);
    setStudentSummaryLoading(true);
    setStudentSummary(null);
    try {
      const data = /^\d+$/.test(lookup)
        ? await learningAnalyticsService.adminStudentLearningSummary(Number(lookup))
        : await learningAnalyticsService.adminStudentLearningSummaryBySearch(lookup);
      setStudentSummary(data);
    } finally {
      setStudentSummaryLoading(false);
    }
  };

  React.useEffect(() => {
    if (initialCourseId > 0) {
      void openCourseDetail(initialCourseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCourseId]);

  const courses = overview?.courses ?? [];
  const coursesPagination = overview?.courses_pagination;

  if (loading && !overview) {
    return (
      <div className="mt-3 space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-3">
        <EmptyState title={isArabic ? "تعذر تحميل التحليلات" : "Failed to load analytics"} description={error} />
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-4">
      <div className="flex items-center gap-2">
        <MdInsights className="h-6 w-6 text-brand-500" />
        <h1 className="text-xl font-bold text-gray-800 dark:text-white">
          {isArabic ? "تحليلات التعلم" : "Learning Analytics"}
        </h1>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <SummaryCard
          icon={<MdPeopleAlt className="h-5 w-5 text-brand-600" />}
          label={isArabic ? "المتعلمون النشطون" : "Active learners"}
          value={overview?.total_active_learners ?? 0}
          tone="from-brand-400/10 to-brand-400/5"
        />
        <SummaryCard
          icon={<MdTrendingUp className="h-5 w-5 text-indigo-600" />}
          label={isArabic ? "متوسط التقدم" : "Average progress"}
          value={`${overview?.average_progress_percentage ?? 0}%`}
          tone="from-indigo-400/10 to-indigo-400/5"
        />
        <SummaryCard
          icon={<MdCheckCircle className="h-5 w-5 text-emerald-600" />}
          label={isArabic ? "إكمالات الكورسات" : "Completed enrollments"}
          value={overview?.completed_course_enrollments_count ?? 0}
          tone="from-emerald-400/10 to-emerald-400/5"
        />
        <SummaryCard
          icon={<MdQuiz className="h-5 w-5 text-amber-600" />}
          label={isArabic ? "متوسط الاختبارات" : "Average quiz score"}
          value={`${overview?.average_quiz_score ?? 0}%`}
          tone="from-amber-400/10 to-amber-400/5"
        />
        <SummaryCard
          icon={<MdSchedule className="h-5 w-5 text-gray-600" />}
          label={isArabic ? "نشطون مؤخراً (7 أيام)" : "Recently active (7d)"}
          value={overview?.recently_active_students_count ?? 0}
          tone="from-gray-400/10 to-gray-400/5"
        />
      </div>

      <Card extra="p-4">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base font-bold text-gray-800 dark:text-white">
            {isArabic ? "الكورسات" : "Courses"}
          </h2>
          <input
            type="search"
            value={courseSearch}
            onChange={(e) => setCourseSearch(e.target.value)}
            placeholder={isArabic ? "بحث بالكورس أو المدرّس..." : "Search course or instructor..."}
            className="w-full max-w-sm rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-900"
          />
        </div>

        <div className={DATA_TABLE_SCROLL}>
          <table className="w-full min-w-[900px]" dir={isArabic ? "rtl" : "ltr"}>
            <thead>
              <tr className={DATA_TABLE_HEADER_ROW}>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "الكورس" : "Course"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "المدرّس" : "Instructor"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "الطلاب" : "Students"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "متوسط التقدم" : "Avg progress"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "متوسط الاختبار" : "Avg quiz"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "مكتملون" : "Completed"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "آخر نشاط" : "Last activity"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "إجراءات" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {courses.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-sm text-gray-500">
                    {isArabic ? "لا توجد كورسات" : "No courses found"}
                  </td>
                </tr>
              ) : (
                courses.map((row) => (
                  <tr key={row.course_id} className={DATA_TABLE_BODY_ROW}>
                    <td className={dataTableBodyCellClass(isArabic)}>
                      <p className="font-medium text-gray-800 dark:text-white">{row.course_title}</p>
                    </td>
                    <td className={dataTableBodyCellClass(isArabic)}>{row.instructor_name ?? "—"}</td>
                    <td className={dataTableBodyCellClass(isArabic)}>{row.students_count}</td>
                    <td className={dataTableBodyCellClass(isArabic)}>{row.average_progress_percentage}%</td>
                    <td className={dataTableBodyCellClass(isArabic)}>{row.average_quiz_score}%</td>
                    <td className={dataTableBodyCellClass(isArabic)}>{row.completed_students_count}</td>
                    <td className={dataTableBodyCellClass(isArabic)}>{formatDate(row.last_activity_at)}</td>
                    <td className={dataTableBodyCellClass(isArabic)}>
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          onClick={() => openCourseDetail(row.course_id)}
                          className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
                        >
                          <MdVisibility className="h-3.5 w-3.5" />
                          {isArabic ? "تحليلات" : "Analytics"}
                        </button>
                        <Link
                          to={`/admin/entities?course_id=${row.course_id}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-white"
                        >
                          <MdMenuBook className="h-3.5 w-3.5" />
                          {isArabic ? "مالي" : "Financial"}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {coursesPagination ? (
          <DataTablePagination
            isArabic={isArabic}
            page={coursesPagination.current_page}
            totalPages={coursesPagination.last_page}
            fromItem={
              coursesPagination.total === 0
                ? 0
                : (coursesPagination.current_page - 1) * coursesPagination.per_page + 1
            }
            toItem={Math.min(
              coursesPagination.current_page * coursesPagination.per_page,
              coursesPagination.total
            )}
            totalItems={coursesPagination.total}
            onPageChange={setCoursesPage}
            className="mt-4"
          />
        ) : null}
      </Card>

      <Card extra="p-4">
        <h2 className="mb-3 text-base font-bold text-gray-800 dark:text-white">
          {isArabic ? "ملخص تعلم الطالب" : "Student learning summary"}
        </h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MdSearch className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 ${isArabic ? "right-3" : "left-3"}`} />
            <input
              type="search"
              value={studentIdInput}
              onChange={(e) => setStudentIdInput(e.target.value)}
              placeholder={isArabic ? "معرّف الطالب أو الاسم أو الهاتف" : "Student ID, name, or phone"}
              className={`w-full rounded-xl border border-gray-200 py-2 text-sm dark:border-white/10 dark:bg-navy-900 ${isArabic ? "pr-9 pl-3" : "pl-9 pr-3"}`}
            />
          </div>
          <button
            type="button"
            onClick={() => openStudentSummary()}
            disabled={!studentIdInput.trim()}
            className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {isArabic ? "عرض الملخص" : "View summary"}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {isArabic
            ? "أدخل معرّف الطالب أو اسمه أو رقم هاتفه لعرض تقدمه عبر الكورسات."
            : "Enter a student ID, name, or phone number to view cross-course progress."}
        </p>
      </Card>

      <SlideOver
        open={courseDetailOpen}
        onClose={() => setCourseDetailOpen(false)}
        title={isArabic ? "تحليلات الكورس" : "Course analytics"}
        width="md"
      >
        {courseDetailLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : courseDetail ? (
          <div className="space-y-3 text-sm">
            <p className="font-bold text-gray-800 dark:text-white">{courseDetail.course_title}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500">{isArabic ? "الطلاب" : "Students"}</p>
                <p className="font-bold">{courseDetail.total_students}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{isArabic ? "متوسط التقدم" : "Avg progress"}</p>
                <p className="font-bold">{courseDetail.average_progress_percentage}%</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{isArabic ? "مكتملون" : "Completed"}</p>
                <p className="font-bold">{courseDetail.completed_students}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{isArabic ? "متوسط الاختبار" : "Avg quiz"}</p>
                <p className="font-bold">{courseDetail.average_quiz_score}%</p>
              </div>
            </div>
            {!courseDetail.has_content ? (
              <p className="text-gray-500">{isArabic ? "لا يوجد محتوى" : "No content"}</p>
            ) : null}
          </div>
        ) : null}
      </SlideOver>

      <SlideOver
        open={studentSummaryOpen}
        onClose={() => setStudentSummaryOpen(false)}
        title={isArabic ? "ملخص تعلم الطالب" : "Student learning summary"}
        width="lg"
      >
        {studentSummaryLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : studentSummary ? (
          <div className="space-y-4">
            <div>
              <p className="font-bold text-gray-800 dark:text-white">{studentSummary.student.name}</p>
              <p className="text-xs text-gray-500">{studentSummary.student.phone}</p>
              <p className="mt-2 text-sm">
                {isArabic ? "متوسط التقدم" : "Average progress"}: {studentSummary.average_progress_percentage}%
              </p>
            </div>
            {studentSummary.courses.length === 0 ? (
              <p className="text-sm text-gray-500">{isArabic ? "لا توجد كورسات" : "No courses"}</p>
            ) : (
              <ul className="space-y-2">
                {studentSummary.courses.map((course) => (
                  <li key={course.course_id} className="rounded-lg border border-gray-200 p-3 dark:border-white/10">
                    <p className="font-medium text-gray-800 dark:text-white">{course.course_title}</p>
                    <p className="text-xs text-gray-500">{course.instructor_name}</p>
                    <p className="mt-1 text-sm">
                      {course.progress_percentage}% · {course.completed_items_count}/{course.total_items_count}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">{isArabic ? "لم يتم العثور على الطالب" : "Student not found"}</p>
        )}
      </SlideOver>
    </div>
  );
}
